/**
 * QuotePresetsAdmin.tsx — Admin > Quotes > Quote Presets (behind the Quotes
 * module), the port of legacy admin/admin_quote_template_list.php: the
 * account's quote presets inside their categories, as a drag-and-drop board
 * like Patient Notes (CategoryBoard):
 *   - categories A→Z, each holding its presets A→Z, with legacy's count of
 *     presets; drag a preset onto another category to move it there (saved on
 *     drop; 2026-10-07, user: the boards file, they don't order);
 *   - "Add category" top right; on a category row: Add preset (legacy's "+"),
 *     Edit (the name), Delete — only once it holds no live presets — and,
 *     under Show deleted, Restore;
 *   - a preset opens legacy's preset editor (QuotePresetFormModal: Name, then
 *     the Procedures, Products and Discounts Load Preset copies onto a
 *     patient's quote); Delete and, under Show deleted, Restore on its row;
 *   - search by name and Show deleted. A preset in a deleted category shows
 *     only under Show deleted, with a "Category Deleted" badge; one with no
 *     category is listed in Uncategorized and can be dragged into one.
 * Legacy's Save All is gone: each change saves as it is made.
 *
 * On a Locked page the shared blocks drop Add category, the row icons and
 * drag (AdminBoardPage, AdminRowAction, CategoryBoard); a preset still opens
 * read-only (AdminFormModal).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { AdminBoardPage, AdminListEmpty, AdminListToolbar } from '@/components/admin/AdminList';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { AdminRowAction } from '@/components/admin/AdminRowActions';
import { CategoryBoard, boardDragHint, type BoardCategory } from '@/components/admin/CategoryBoard';
import { CategoryNameModal } from '@/components/admin/CategoryNameModal';
import { compareTitles, isSeparatorTitle } from '@/components/admin/categoryBoardState';
import { OutlineBadge } from '@/components/admin/CorpBadges';
import { QuotePresetFormModal } from '@/components/admin/QuotePresetFormModal';
import { getErrorMessage } from '@/services/apiClient';
import {
  createQuotePresetCategory,
  deleteQuotePreset,
  deleteQuotePresetCategory,
  listQuotePresetCategories,
  listQuotePresets,
  restoreQuotePreset,
  restoreQuotePresetCategory,
  updateQuotePreset,
  updateQuotePresetCategory,
  type QuotePreset,
  type QuotePresetCategory,
} from '@/services/quotePresetsAdmin.service';
import { toast } from '@/store/toastStore';

const SUBTITLE = 'Quote presets grouped by category.';
/** Added to the subtitle while presets can be dragged (not on a Locked page). */
const DRAG_SUBTITLE = 'Drag a preset onto another category to move it there.';

function presetName(p: QuotePreset): string {
  return p.name.trim() || `Preset #${p.quoteTemplateId}`;
}

const getRecordId = (p: QuotePreset) => p.quoteTemplateId;
const getRecordTitle = (p: QuotePreset) => p.name;
const getRecordCategoryId = (p: QuotePreset) => p.quoteTemplateCategoryId;
const isDeleted = (p: QuotePreset) => !p.active;

type FormState = { preset: QuotePreset | null; category: { id: number; name: string } | null } | null;
type CategoryModalState = { current: QuotePresetCategory | null } | null;

export function QuotePresetsAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [presets, setPresets] = useState<QuotePreset[]>([]);
  const [categories, setCategories] = useState<QuotePresetCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [formState, setFormState] = useState<FormState>(null);
  const [categoryModal, setCategoryModal] = useState<CategoryModalState>(null);
  const [deletingPreset, setDeletingPreset] = useState<QuotePreset | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<BoardCategory | null>(null);

  // Only the latest load may write.
  const loadSeq = useRef(0);
  const load = useCallback(async (quiet = false) => {
    const seq = ++loadSeq.current;
    if (!quiet) setLoading(true);
    try {
      const [list, cats] = await Promise.all([listQuotePresets(), listQuotePresetCategories()]);
      if (seq !== loadSeq.current) return;
      setPresets(list);
      setCategories(cats);
      setLoadError(null);
    } catch (err) {
      if (seq === loadSeq.current) {
        setLoadError(
          getErrorMessage(err, 'Quote presets could not be loaded. Refresh the page to try again. If it keeps happening, contact Prospera support.'),
        );
      }
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // ── Board data ──────────────────────────────────────────────────────────────

  const liveCategories = useMemo(() => categories.filter((c) => c.active), [categories]);
  const liveCategoryIds = useMemo(() => new Set(liveCategories.map((c) => c.categoryId)), [liveCategories]);
  const deletedCategoryIds = useMemo(
    () => new Set(categories.filter((c) => !c.active).map((c) => c.categoryId)),
    [categories],
  );

  const allBoardCategories = useMemo(
    (): BoardCategory[] =>
      categories
        .filter((c) => (showDeleted || c.active) && !isSeparatorTitle(c.name))
        .sort((a, b) => compareTitles(a.name, b.name))
        .map((c) => ({ id: c.categoryId, title: c.name, deleted: !c.active })),
    [categories, showDeleted],
  );

  /** A preset filed under a deleted category: shown only under Show deleted. */
  const inDeletedCategory = useCallback(
    (p: QuotePreset) =>
      p.quoteTemplateCategoryId != null &&
      deletedCategoryIds.has(p.quoteTemplateCategoryId) &&
      !liveCategoryIds.has(p.quoteTemplateCategoryId),
    [deletedCategoryIds, liveCategoryIds],
  );

  const listed = useMemo(
    () => presets.filter((p) => showDeleted || (p.active && !inDeletedCategory(p))),
    [presets, showDeleted, inDeletedCategory],
  );

  const query = search.trim().toLowerCase();
  const searching = query !== '';
  const records = useMemo(
    () => (searching ? listed.filter((p) => p.name.toLowerCase().includes(query)) : listed),
    [listed, searching, query],
  );
  const boardCategories = useMemo(() => {
    if (!searching) return allBoardCategories;
    const withMatches = new Set(records.map((p) => p.quoteTemplateCategoryId));
    return allBoardCategories.filter((c) => withMatches.has(c.id));
  }, [allBoardCategories, records, searching]);

  /** Live presets filed under a category — a category is deleted only once it has none. */
  const liveIn = useCallback(
    (categoryId: number) => presets.filter((p) => p.active && p.quoteTemplateCategoryId === categoryId).length,
    [presets],
  );

  const categoryName = useCallback(
    (id: number | null) => categories.find((c) => c.categoryId === id)?.name ?? null,
    [categories],
  );

  // ── Saves ───────────────────────────────────────────────────────────────────

  /** A drop: only the moved preset (the one not yet in this category) is saved; the server files it last. */
  const handleMove = useCallback(
    async (categoryId: number, ids: number[]) => {
      const moved = presets.filter((p) => ids.includes(p.quoteTemplateId) && p.quoteTemplateCategoryId !== categoryId);
      const saved = await Promise.all(
        moved.map((p) => updateQuotePreset(p.quoteTemplateId, { quoteTemplateCategoryId: categoryId })),
      );
      const byId = new Map(saved.map((p) => [p.quoteTemplateId, p]));
      setPresets((prev) => prev.map((p) => byId.get(p.quoteTemplateId) ?? p));
    },
    [presets],
  );

  const handleSaveError = useCallback((err: unknown) => {
    toast.error(getErrorMessage(err, 'The preset could not be moved. Try again.'));
  }, []);

  function handlePresetSaved(saved: QuotePreset, isNew: boolean) {
    setFormState(null);
    setPresets((prev) =>
      isNew ? [...prev, saved] : prev.map((p) => (p.quoteTemplateId === saved.quoteTemplateId ? saved : p)),
    );
  }

  async function handleDeletePreset() {
    if (!deletingPreset) return;
    const id = deletingPreset.quoteTemplateId;
    try {
      await deleteQuotePreset(id);
      setPresets((prev) => prev.map((p) => (p.quoteTemplateId === id ? { ...p, active: false } : p)));
      toast.success(`"${presetName(deletingPreset)}" deleted`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'The quote preset could not be deleted. Try again.'));
    }
  }

  async function handleRestorePreset(preset: QuotePreset) {
    try {
      const saved = await restoreQuotePreset(preset.quoteTemplateId);
      setPresets((prev) => prev.map((p) => (p.quoteTemplateId === preset.quoteTemplateId ? saved : p)));
      toast.success(`${presetName(preset)} restored`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'The quote preset could not be restored. Try again.'));
    }
  }

  async function handleCategorySave(name: string) {
    const current = categoryModal?.current ?? null;
    if (current) {
      if (name !== current.name) {
        const saved = await updateQuotePresetCategory(current.categoryId, { name });
        setCategories((prev) => prev.map((c) => (c.categoryId === current.categoryId ? saved : c)));
      }
      toast.success('Category updated');
    } else {
      const saved = await createQuotePresetCategory(name);
      setCategories((prev) => [...prev, saved]);
      toast.success('Category created');
    }
    setCategoryModal(null);
  }

  async function handleDeleteCategory() {
    if (!deletingCategory) return;
    const id = deletingCategory.id;
    try {
      await deleteQuotePresetCategory(id);
      setCategories((prev) => prev.map((c) => (c.categoryId === id ? { ...c, active: false } : c)));
      toast.success('Category deleted');
    } catch (err) {
      toast.error(getErrorMessage(err, 'The category could not be deleted.'));
    }
  }

  async function handleRestoreCategory(c: BoardCategory) {
    try {
      const saved = await restoreQuotePresetCategory(c.id);
      setCategories((prev) => prev.map((x) => (x.categoryId === c.id ? saved : x)));
      toast.success(`${c.title} restored`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'The category could not be restored.'));
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  const renderRecord = useCallback(
    (p: QuotePreset) => {
      const deleted = !p.active;
      return (
        <button
          type="button"
          // A deleted preset is restored, not edited.
          disabled={deleted}
          onClick={() => setFormState({ preset: p, category: null })}
          className="flex w-full items-center gap-2 rounded py-2 pr-2 text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default disabled:hover:bg-transparent"
        >
          <span data-testid="quote-preset-name" className="truncate text-sm font-medium text-slate-700">
            {presetName(p)}
          </span>
          {inDeletedCategory(p) && (
            <OutlineBadge tone="orange" className="shrink-0">
              Category Deleted
            </OutlineBadge>
          )}
        </button>
      );
    },
    [inDeletedCategory],
  );

  function renderCategoryActions(c: BoardCategory) {
    if (c.deleted) return <AdminRowAction kind="restore" label={`Restore ${c.title}`} onClick={() => void handleRestoreCategory(c)} />;
    const count = liveIn(c.id);
    const category = categories.find((x) => x.categoryId === c.id) ?? null;
    return (
      <>
        <AdminRowAction
          kind="add"
          title="Add preset"
          label={`Add preset to ${c.title}`}
          onClick={() => setFormState({ preset: null, category: { id: c.id, name: c.title } })}
        />
        <AdminRowAction kind="edit" label={`Edit ${c.title}`} onClick={() => setCategoryModal({ current: category })} />
        <AdminRowAction
          kind="delete"
          label={`Delete ${c.title}`}
          blockedReason={count > 0 ? `Holds ${count} ${count === 1 ? 'preset' : 'presets'} — move or delete them first` : null}
          onClick={() =>
            count > 0 ? toast.error(`Move or delete the presets in "${c.title}" before deleting it.`) : setDeletingCategory(c)
          }
        />
      </>
    );
  }

  function renderRecordActions(p: QuotePreset) {
    const name = presetName(p);
    if (!p.active) return <AdminRowAction kind="restore" label={`Restore ${name}`} onClick={() => void handleRestorePreset(p)} />;
    return (
      <>
        <AdminRowAction kind="edit" label={`Edit ${name}`} onClick={() => setFormState({ preset: p, category: null })} />
        <AdminRowAction kind="delete" label={`Delete ${name}`} onClick={() => setDeletingPreset(p)} />
      </>
    );
  }

  const columnHeader = (
    <div className="flex items-center gap-2 pr-2">
      <span className="min-w-0 flex-1">Name</span>
    </div>
  );

  const formCategory = formState
    ? formState.preset
      ? (() => {
          const id = formState.preset.quoteTemplateCategoryId;
          const name = categoryName(id);
          return id && name ? { id, name } : null;
        })()
      : formState.category
    : null;

  return (
    <AdminBoardPage
      title="Quote Presets"
      subtitle={locked ? SUBTITLE : `${SUBTITLE} ${DRAG_SUBTITLE}`}
      actions={
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setCategoryModal({ current: null })}>
          Add category
        </Button>
      }
      toolbar={
        <AdminListToolbar
          className="mb-0"
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search presets…"
          searchLabel="Search quote presets"
          showDeleted={showDeleted}
          onShowDeletedChange={setShowDeleted}
          showDeletedId="quote-presets-show-deleted"
        />
      }
      hint={boardDragHint({ searching, filtering: false, showDeleted })}
      dialogs={
        <>
          {formState && (
            <QuotePresetFormModal
              key={formState.preset ? `edit-${formState.preset.quoteTemplateId}` : `new-${formState.category?.id}`}
              preset={formState.preset}
              category={formCategory}
              onClose={() => setFormState(null)}
              onSaved={handlePresetSaved}
            />
          )}
          {categoryModal && (
            <CategoryNameModal
              current={categoryModal.current ? { id: categoryModal.current.categoryId, name: categoryModal.current.name } : null}
              categories={liveCategories.map((c) => ({ id: c.categoryId, name: c.name }))}
              recordNoun="quote preset"
              onSave={handleCategorySave}
              onClose={() => setCategoryModal(null)}
            />
          )}
          <ConfirmDialog
            isOpen={deletingPreset !== null}
            onClose={() => setDeletingPreset(null)}
            onConfirm={handleDeletePreset}
            title={deletingPreset ? `Delete "${presetName(deletingPreset)}"?` : ''}
            message="It will no longer be offered when loading a preset onto a quote. Quotes already built from it don't change. You can restore it from Show deleted."
            confirmLabel="Delete preset"
            variant="danger"
          />
          <ConfirmDialog
            isOpen={deletingCategory !== null}
            onClose={() => setDeletingCategory(null)}
            onConfirm={handleDeleteCategory}
            title={deletingCategory ? `Delete "${deletingCategory.title}"?` : ''}
            message="You can restore it from Show deleted."
            confirmLabel="Delete category"
            variant="danger"
          />
        </>
      }
    >
      {loadError ? (
        <p role="alert" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          {loadError}
        </p>
      ) : loading ? (
        <SpinnerOverlay label="Loading quote presets…" />
      ) : searching && records.length === 0 ? (
        <AdminListEmpty title="No quote presets found" hint="Try adjusting your search." />
      ) : (
        <>
          {!searching && boardCategories.length === 0 && records.length === 0 && (
            <AdminListEmpty title="No categories yet" hint={locked ? undefined : 'Add a category, then add quote presets to it.'} />
          )}
          <CategoryBoard<QuotePreset>
            categories={boardCategories}
            records={records}
            getRecordId={getRecordId}
            getRecordTitle={getRecordTitle}
            getRecordCategoryId={getRecordCategoryId}
            renderRecord={renderRecord}
            columnHeader={columnHeader}
            renderCategoryActions={renderCategoryActions}
            renderRecordActions={renderRecordActions}
            isRecordDeleted={isDeleted}
            onReorderRecords={handleMove}
            onSaveError={handleSaveError}
            recordNoun={['preset', 'presets']}
            dragDisabled={searching || showDeleted}
          />
        </>
      )}
    </AdminBoardPage>
  );
}
